// pages/UserLogin.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/SetNavbar";
import {
  Grid,
  Typography,
  Container,
  Box,
  Link,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Paper,
  Chip,
  Fade,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  School,
  ArrowForward,
  Google,
  Facebook,
  LinkedIn,
  Verified,
  Star,
} from "@mui/icons-material";
import { postData } from "../../services/Request";
import CircularProgress from "@mui/material/CircularProgress";
import S1 from "../../assets/ContactUs/L1.png";
import "../../styles/SignUp.css";

export default function UserLogIn() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [alertMessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("");
  const [loader, setLoader] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    // Check if user is already logged in
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        if (data && data.usertype === "Student") {
          nav("/courses");
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, [nav]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (password) => {
    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value) validateEmail(value);
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (value) validatePassword(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);

    if (!isEmailValid || !isPasswordValid) {
      return;
    }

    setLoader(true);
    setAlertMessage("");

    const details = { email, password };

    postData("userlogin", details)
      .then((response) => {
        setLoader(false);
        if (response.status === true) {
          localStorage.setItem("user", JSON.stringify(response.user));
          if (rememberMe) {
            localStorage.setItem("rememberedEmail", email);
          }
          setAlertMessage("✅ Logging you in...");
          setSeverity("success");
          setTimeout(() => {
            nav("/courses");
          }, 500);
        } else {
          setAlertMessage("❌ " + (response.message || "Invalid credentials"));
          setSeverity("error");
        }
      })
      .catch((error) => {
        setLoader(false);
        setAlertMessage(
          "❌ Error logging you in, please check your account is activated",
        );
        setSeverity("error");
        console.error("Login error:", error);
      });
  };

  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Header />
      <Box
        className="login-container"
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0a1628 0%, #0d1f3c 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          py: { xs: 4, md: 0 },
        }}
      >
        {/* Animated Background Decorations */}
        <Box
          sx={{
            position: "absolute",
            top: -200,
            right: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(78, 205, 196, 0.05) 0%, transparent 70%)",
            zIndex: 0,
            animation: "pulse 8s ease-in-out infinite",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -200,
            left: -200,
            width: 400,
            height: 400,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(69, 183, 209, 0.04) 0%, transparent 70%)",
            zIndex: 0,
            animation: "pulse 8s ease-in-out infinite 2s",
          }}
        />

        {/* Floating Particles */}
        {[...Array(4)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${10 + i * 25}%`,
              left: `${5 + (i % 3) * 30}%`,
              width: `${4 + i * 2}px`,
              height: `${4 + i * 2}px`,
              borderRadius: "50%",
              backgroundColor:
                i % 2 === 0
                  ? "rgba(78, 205, 196, 0.15)"
                  : "rgba(69, 183, 209, 0.15)",
              animation: `float ${6 + i * 2}s ease-in-out infinite ${i * 1.5}s`,
              zIndex: 0,
            }}
          />
        ))}

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: "hidden",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              maxWidth: 1100,
              mx: "auto",
            }}
          >
            <Grid container>
              {/* Left Side - Image & Branding */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: { xs: "none", md: "flex" },
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  background:
                    "linear-gradient(135deg, rgba(78, 205, 196, 0.05), rgba(69, 183, 209, 0.02))",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                {/* Image */}
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 400,
                  }}
                >
                  <Box
                    component="img"
                    src={S1}
                    alt="Student Login"
                    sx={{
                      width: "100%",
                      height: "auto",
                      borderRadius: 3,
                      border: "1px solid rgba(255, 255, 255, 0.06)",
                      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                      transition: "transform 0.5s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                      },
                    }}
                  />

                  {/* Floating Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 20,
                      left: 20,
                      backgroundColor: "rgba(10, 22, 40, 0.85)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(78, 205, 196, 0.2)",
                      borderRadius: 2,
                      p: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "rgba(78, 205, 196, 0.15)",
                        borderRadius: "50%",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <School sx={{ color: "#4ECDC4" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.3)" }}
                      >
                        Active Students
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#ffffff", fontWeight: 700 }}
                      >
                        10,000+
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Trust Badges */}
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ mt: 4, flexWrap: "wrap", justifyContent: "center" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Verified sx={{ color: "#4ECDC4", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.3)" }}
                    >
                      Secure Login
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Star sx={{ color: "#FFD93D", fontSize: 16 }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "rgba(255,255,255,0.3)" }}
                    >
                      4.8/5 Rating
                    </Typography>
                  </Box>
                </Stack>
              </Grid>

              {/* Right Side - Login Form */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Chip
                      label="🎓 Student Login"
                      sx={{
                        backgroundColor: "rgba(78, 205, 196, 0.15)",
                        color: "#4ECDC4",
                        fontWeight: 600,
                        mb: 2,
                        border: "1px solid rgba(78, 205, 196, 0.2)",
                      }}
                    />
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: "#ffffff",
                        fontSize: { xs: "1.8rem", md: "2.2rem" },
                      }}
                    >
                      Welcome Back!
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255, 255, 255, 0.5)",
                        mt: 0.5,
                      }}
                    >
                      Access your personalized tech learning experience with a
                      click
                    </Typography>
                  </Box>

                  {/* Alert Message */}
                  {alertMessage && (
                    <Fade in timeout={300}>
                      <Alert
                        variant="filled"
                        severity={severity}
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          backgroundColor:
                            severity === "success" ? "#4ECDC4" : "#FF6B6B",
                          "& .MuiAlert-icon": {
                            color: "#ffffff",
                          },
                        }}
                      >
                        {alertMessage}
                      </Alert>
                    </Fade>
                  )}

                  {/* Form */}
                  <Box component="form" onSubmit={handleSubmit} noValidate>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={handleEmailChange}
                      error={!!emailError}
                      helperText={emailError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: "rgba(255,255,255,0.2)" }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.04)",
                          color: "#ffffff",
                          borderRadius: 2,
                          "& fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.06)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.12)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#4ECDC4",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255, 255, 255, 0.4)",
                        },
                        "& .MuiFormHelperText-root": {
                          color: "#FF6B6B",
                        },
                      }}
                    />

                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      id="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={handlePasswordChange}
                      error={!!passwordError}
                      helperText={passwordError}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: "rgba(255,255,255,0.2)" }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePassword}
                              edge="end"
                              sx={{ color: "rgba(255,255,255,0.2)" }}
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.04)",
                          color: "#ffffff",
                          borderRadius: 2,
                          "& fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.06)",
                          },
                          "&:hover fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.12)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#4ECDC4",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          color: "rgba(255, 255, 255, 0.4)",
                        },
                        "& .MuiFormHelperText-root": {
                          color: "#FF6B6B",
                        },
                      }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            sx={{
                              color: "rgba(255,255,255,0.2)",
                              "&.Mui-checked": {
                                color: "#4ECDC4",
                              },
                            }}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ color: "rgba(255,255,255,0.3)" }}
                          >
                            Remember me
                          </Typography>
                        }
                      />
                      <Link
                        href="#"
                        variant="body2"
                        sx={{
                          color: "#4ECDC4",
                          textDecoration: "none",
                          "&:hover": {
                            color: "#45B7D1",
                          },
                        }}
                      >
                        Forgot password?
                      </Link>
                    </Box>

                    {/* Login Button */}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loader}
                      startIcon={!loader ? <ArrowForward /> : null}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 700,
                        background: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
                        borderRadius: 2,
                        boxShadow: "0 4px 25px rgba(78, 205, 196, 0.3)",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 35px rgba(78, 205, 196, 0.4)",
                          background:
                            "linear-gradient(135deg, #45B7D1, #4ECDC4)",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(78, 205, 196, 0.2)",
                        },
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      {loader ? (
                        <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                      ) : (
                        "Log In"
                      )}
                    </Button>

                    {/* Divider */}
                    <Box sx={{ my: 2 }}>
                      <Divider
                        sx={{ backgroundColor: "rgba(255,255,255,0.06)" }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.2)", px: 2 }}
                        >
                          Or continue with
                        </Typography>
                      </Divider>
                    </Box>

                    {/* Sign Up Link */}
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.3)" }}
                      >
                        Don't have an account?{" "}
                        <Link
                          href="/usersignup"
                          sx={{
                            color: "#4ECDC4",
                            fontWeight: 600,
                            textDecoration: "none",
                            "&:hover": {
                              color: "#45B7D1",
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Sign Up
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Container>

        <style>
          {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.3; }
            50% { transform: scale(1.1); opacity: 0.6; }
          }

          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }

          .login-container {
            min-height: 100vh;
          }
        `}
        </style>
      </Box>
    </>
  );
}
